import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IdBadge from "components/IdBadge";
import { useMemo, useState } from "react";
import ColorScaleDetailsDrawer from "./ColorScaleDetailsDrawer";
import { GradientSwatch } from "./ColorScalePicker";
import CreateColorScaleDialog from "./CreateColorScaleDialog";
import { regionKey, useLidarClearance } from "./LidarClearanceContext";
import { buildGradient } from "./colorScaleDisplay";
import t from "./translations";

const COLUMN_COUNT = 3;

export default function ColorScalesTab() {
  const { colorScales, loading } = useLidarClearance();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const selectedScale =
    colorScales.find(
      (scale) => regionKey(scale.region, scale.color_scale_id) === selectedKey
    ) ?? null;

  const rows = useMemo(
    () =>
      colorScales.map((scale) => ({
        scale,
        gradient: buildGradient(scale.colors, 1, scale.colors.length),
      })),
    [colorScales]
  );

  return (
    <Box data-testid="color-scales-tab">
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          data-testid="color-scales-tab.new-color-scale"
        >
          {t.NewColorScaleButton}
        </Button>
      </Box>
      <TableContainer component={Paper} data-testid="color-scales-table">
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: 90 }}>
                {t.IdColumn}
              </TableCell>
              <TableCell sx={{ width: 90 }} align="right">
                {t.StopsColumn}
              </TableCell>
              <TableCell>{t.PreviewColumn}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {colorScales.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
            {rows.map(({ scale, gradient }) => {
              const key = regionKey(scale.region, scale.color_scale_id);

              return (
                <TableRow
                  key={key}
                  hover
                  onClick={() => setSelectedKey(key)}
                  sx={{ cursor: "pointer" }}
                  data-testid={`color-scales-tab.row-${scale.region}-${scale.color_scale_id}`}
                >
                  <TableCell align="center">
                    <IdBadge
                      prefix={scale.region.toUpperCase()}
                      id={scale.color_scale_id}
                    />
                  </TableCell>
                  <TableCell align="right">{scale.colors.length}</TableCell>
                  <TableCell>
                    <GradientSwatch gradient={gradient} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ColorScaleDetailsDrawer
        scale={selectedScale}
        onClose={() => setSelectedKey(null)}
      />
      <CreateColorScaleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
